import React, { useCallback, useRef } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { Box, Typography } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { useGoogleMapsLoader } from '../../hook/useMapLoader';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { theme } from '../../constants/theme';

interface EventLocationMapProps {
    lat: number;
    lng: number;
    locationName?: string;
}

const containerStyle = {
    width: '100%',
    height: '220px',
};

const DARK_MAP_STYLES: google.maps.MapTypeStyle[] = [
    { elementType: 'geometry', stylers: [{ color: '#1a1a2e' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#1a1a2e' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#8e8ea0' }] },
    { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2c2c4a' }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1a1a2e' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3a3a5c' }] },
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1a1a2e' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e0e1a' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#4a4a6a' }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#22223a' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#8e8ea0' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#1a2a1a' }] },
    { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#22223a' }] },
];

const LIGHT_MAP_STYLES: google.maps.MapTypeStyle[] = [];

const EventLocationMap: React.FC<EventLocationMapProps> = ({ lat, lng, locationName }) => {
    const { themeStyle, darkModeEnabled } = useThemeContext();
    const { isLoaded } = useGoogleMapsLoader();
    const mapRef = useRef<google.maps.Map | null>(null);

    const center = { lat, lng };

    const onLoad = useCallback((map: google.maps.Map) => {
        mapRef.current = map;
    }, []);

    if (!isLoaded) {
        return (
            <Box
                sx={{
                    width: '100%',
                    height: 220,
                    bgcolor: theme[themeStyle].card_grey,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 13,
                        color: theme[themeStyle].tag_white,
                        opacity: 0.5,
                    }}>
                    Loading map…
                </Typography>
            </Box>
        );
    }

    return (
        <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={15}
            onLoad={onLoad}
            options={{
                styles: darkModeEnabled ? DARK_MAP_STYLES : LIGHT_MAP_STYLES,
                disableDefaultUI: true,
                zoomControl: true,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
            }}>
            <Marker position={center} title={locationName} />
        </GoogleMap>
    );
};

export { EventLocationMap };
